import * as ImagePicker from 'expo-image-picker';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import type { AppTheme, ProfilePhotoModalPalette } from '../theme/appearanceTokens';
import { getAppearanceTokens } from '../theme/appearanceTokens';
import {
  PROFILE_PHOTO_MAX_BYTES,
  PROFILE_PHOTO_MAX_SIDE,
  PROFILE_PHOTO_MIN_SIDE,
  PROFILE_PHOTO_RECOMMENDED_IDEAL,
  PROFILE_PHOTO_RECOMMENDED_MIN,
  formatBytes,
  validateProfilePhoto,
  type ProfilePhotoPickMetadata,
} from '../utils/profilePhotoValidation';
import { getImageDimensionsFromUri } from '../utils/getImageDimensionsFromUri';

export interface ProfilePhotoValidatedPayload extends ProfilePhotoPickMetadata {
  readonly uri: string;
}

interface ProfilePhotoUploadModalProps {
  readonly visible: boolean;
  readonly theme: AppTheme;
  readonly onClose: () => void;
  readonly onValidationSuccess?: (payload: ProfilePhotoValidatedPayload) => Promise<void> | void;
}

interface PendingAsset {
  readonly uri: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly width: number;
  readonly height: number;
}

function createModalStyles(p: ProfilePhotoModalPalette) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: p.overlay,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    overlayDismiss: {
      ...StyleSheet.absoluteFillObject,
    },
    sheet: {
      width: '100%',
      maxWidth: 440,
      flexDirection: 'column',
      backgroundColor: p.sheetBackground,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: p.sheetBorder,
      padding: 20,
      zIndex: 2,
      shadowColor: p.shadowColor,
      shadowOpacity: 0.2,
      shadowRadius: 20,
      shadowOffset: { width: 0, height: 8 },
      elevation: 12,
    },
    title: {
      fontSize: 20,
      color: p.title,
      fontFamily: 'Montserrat_600SemiBold',
      marginBottom: 6,
      flexShrink: 0,
    },
    subtitle: {
      fontSize: 13,
      color: p.subtitle,
      fontFamily: 'Montserrat_500Medium',
      lineHeight: 18,
      marginBottom: 12,
      flexShrink: 0,
    },
    scrollArea: {
      width: '100%',
    },
    scrollContent: {
      paddingBottom: 4,
    },
    guidelines: {
      backgroundColor: p.guidelinesBackground,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: p.guidelinesBorder,
      padding: 12,
      marginBottom: 14,
    },
    guidelinesTitle: {
      fontSize: 13,
      color: p.guidelinesTitle,
      fontFamily: 'Montserrat_600SemiBold',
      marginBottom: 8,
    },
    guidelinesLine: {
      fontSize: 12,
      color: p.guidelinesLine,
      fontFamily: 'Montserrat_500Medium',
      lineHeight: 17,
      marginBottom: 4,
    },
    chooseButton: {
      backgroundColor: p.chooseButton,
      borderRadius: 10,
      paddingVertical: 12,
      alignItems: 'center',
      marginBottom: 14,
    },
    chooseButtonDisabled: {
      opacity: 0.6,
    },
    chooseButtonText: {
      color: p.chooseButtonText,
      fontSize: 14,
      fontFamily: 'Montserrat_600SemiBold',
    },
    previewBlock: {
      alignItems: 'center',
      marginTop: 12,
      marginBottom: 4,
      flexShrink: 0,
    },
    previewImage: {
      width: 160,
      height: 160,
      borderRadius: 80,
      backgroundColor: p.previewRing,
      marginBottom: 8,
    },
    previewMeta: {
      fontSize: 13,
      color: p.previewMeta,
      fontFamily: 'Montserrat_500Medium',
    },
    errorText: {
      fontSize: 13,
      color: p.errorText,
      fontFamily: 'Montserrat_500Medium',
      marginBottom: 8,
      lineHeight: 18,
    },
    successText: {
      fontSize: 13,
      color: p.successText,
      fontFamily: 'Montserrat_500Medium',
      marginBottom: 8,
      lineHeight: 18,
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginTop: 10,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: p.footerBorder,
      flexShrink: 0,
    },
    cancelButton: {
      marginRight: 10,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: p.cancelBorder,
      backgroundColor: p.cancelBackground,
    },
    cancelButtonText: {
      fontSize: 14,
      color: p.cancelText,
      fontFamily: 'Montserrat_600SemiBold',
    },
    acceptButton: {
      paddingHorizontal: 18,
      paddingVertical: 10,
      borderRadius: 10,
      backgroundColor: p.acceptBackground,
    },
    acceptButtonDisabled: {
      opacity: 0.45,
    },
    acceptButtonText: {
      fontSize: 14,
      color: p.acceptText,
      fontFamily: 'Montserrat_600SemiBold',
    },
    doneButton: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 10,
      backgroundColor: p.doneBackground,
      alignItems: 'center',
    },
    doneButtonText: {
      fontSize: 14,
      color: p.doneText,
      fontFamily: 'Montserrat_600SemiBold',
    },
  });
}

async function resolveSizeBytes(uri: string, known: number | null | undefined): Promise<number> {
  if (typeof known === 'number' && known > 0) {
    return known;
  }
  const response = await fetch(uri);
  const blob = await response.blob();
  return blob.size;
}

function openWebFilePicker(): Promise<File | null> {
  return new Promise(resolve => {
    if (typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
    input.onchange = () => {
      const file = input.files?.[0] ?? null;
      input.value = '';
      resolve(file);
    };
    input.oncancel = () => {
      resolve(null);
    };
    input.click();
  });
}

export const ProfilePhotoUploadModal: React.FC<ProfilePhotoUploadModalProps> = ({
  visible,
  theme,
  onClose,
  onValidationSuccess,
}) => {
  const palette = useMemo(() => getAppearanceTokens(theme).modalProfilePhoto, [theme]);
  const styles = useMemo(() => createModalStyles(palette), [palette]);

  const { height: windowHeight } = useWindowDimensions();
  const sheetMaxHeight = Math.min(windowHeight * 0.92, windowHeight - 24);

  const [pending, setPending] = useState<PendingAsset | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const resetState = useCallback(() => {
    setPending(current => {
      if (current?.uri.startsWith('blob:')) {
        URL.revokeObjectURL(current.uri);
      }
      return null;
    });
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsBusy(false);
  }, []);

  useEffect(() => {
    if (!visible) {
      resetState();
    }
  }, [visible, resetState]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleChoosePhoto = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsBusy(true);

    try {
      if (Platform.OS === 'web') {
        const file = await openWebFilePicker();
        if (!file) {
          setIsBusy(false);
          return;
        }
        const mimeType = file.type && file.type.length > 0 ? file.type : 'image/jpeg';
        const uri = URL.createObjectURL(file);
        const { width, height } = await getImageDimensionsFromUri(uri);
        const sizeBytes = file.size;
        setPending(previous => {
          if (previous?.uri.startsWith('blob:')) {
            URL.revokeObjectURL(previous.uri);
          }
          return { uri, mimeType, sizeBytes, width, height };
        });
        setIsBusy(false);
        return;
      }

      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        setErrorMessage('Photo library access is required to choose an image.');
        setIsBusy(false);
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        quality: 1,
      });

      if (result.canceled || !result.assets[0]) {
        setIsBusy(false);
        return;
      }

      const asset = result.assets[0];
      const uri = asset.uri;
      let width = asset.width ?? 0;
      let height = asset.height ?? 0;
      if (width < 1 || height < 1) {
        const dim = await getImageDimensionsFromUri(uri);
        width = dim.width;
        height = dim.height;
      }
      const mimeType =
        asset.mimeType && asset.mimeType.length > 0 ? asset.mimeType : 'image/jpeg';
      let sizeBytes: number;
      try {
        sizeBytes = await resolveSizeBytes(uri, asset.fileSize ?? null);
      } catch {
        setErrorMessage('Could not read file size. Try another image.');
        setIsBusy(false);
        return;
      }

      setPending(previous => {
        if (previous?.uri.startsWith('blob:')) {
          URL.revokeObjectURL(previous.uri);
        }
        return { uri, mimeType, sizeBytes, width, height };
      });
    } catch (error) {
      setErrorMessage('Could not read the selected image. Try another file.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleAccept = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!pending) {
      setErrorMessage('Choose a photo first.');
      return;
    }

    const validation = validateProfilePhoto({
      mimeType: pending.mimeType,
      sizeBytes: pending.sizeBytes,
      width: pending.width,
      height: pending.height,
    });

    if (!validation.valid) {
      setErrorMessage(validation.errors.join(' '));
      return;
    }

    try {
      setIsBusy(true);
      await onValidationSuccess?.({
        uri: pending.uri,
        mimeType: pending.mimeType,
        sizeBytes: pending.sizeBytes,
        width: pending.width,
        height: pending.height,
      });

      setSuccessMessage('Photo updated successfully.');
    } catch (error) {
      setErrorMessage('Could not upload the selected image. Please try again.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleDoneAfterSuccess = () => {
    resetState();
    onClose();
  };

  const scrollAreaMaxHeight = useMemo(() => {
    const sheetVerticalPadding = 40;
    const titleSubtitleBlock = 118;
    const footerBlock = 78;
    const previewBlock = pending && !successMessage ? 196 : 0;
    const reserved =
      sheetVerticalPadding + titleSubtitleBlock + footerBlock + previewBlock;
    return Math.max(100, sheetMaxHeight - reserved);
  }, [pending, successMessage, sheetMaxHeight]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <View style={styles.overlay}>
        <TouchableOpacity style={styles.overlayDismiss} activeOpacity={1} onPress={handleClose} />
        <View style={[styles.sheet, { maxHeight: sheetMaxHeight }]}>
          <Text style={styles.title}>Change profile photo</Text>
          <Text style={styles.subtitle}>
            Choose an image. On Accept we validate format, size and dimensions, and then save it to
            your profile.
          </Text>

          <ScrollView
            style={[styles.scrollArea, { maxHeight: scrollAreaMaxHeight }]}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator
            keyboardShouldPersistTaps="handled"
            bounces={false}
          >
            <View style={styles.guidelines}>
              <Text style={styles.guidelinesTitle}>Guidelines</Text>
              <Text style={styles.guidelinesLine}>
                • Square images (1:1) look best in the circular avatar.
              </Text>
              <Text style={styles.guidelinesLine}>
                • Recommended: at least {PROFILE_PHOTO_RECOMMENDED_MIN}×{PROFILE_PHOTO_RECOMMENDED_MIN}{' '}
                px; ideal {PROFILE_PHOTO_RECOMMENDED_IDEAL}×{PROFILE_PHOTO_RECOMMENDED_IDEAL} px or
                larger (up to {PROFILE_PHOTO_MAX_SIDE} px per side).
              </Text>
              <Text style={styles.guidelinesLine}>
                • Maximum file size: {formatBytes(PROFILE_PHOTO_MAX_BYTES)}.
              </Text>
              <Text style={styles.guidelinesLine}>• Formats: JPEG, PNG, WebP.</Text>
              <Text style={styles.guidelinesLine}>
                • Minimum dimensions: {PROFILE_PHOTO_MIN_SIDE}×{PROFILE_PHOTO_MIN_SIDE} px.
              </Text>
            </View>

            <TouchableOpacity
              style={[styles.chooseButton, isBusy && styles.chooseButtonDisabled]}
              onPress={handleChoosePhoto}
              disabled={isBusy || !!successMessage}
              activeOpacity={0.85}
            >
              {isBusy ? (
                <ActivityIndicator color={palette.activityOnChoose} />
              ) : (
                <Text style={styles.chooseButtonText}>
                  {pending ? 'Choose a different photo' : 'Choose photo'}
                </Text>
              )}
            </TouchableOpacity>

            {errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}
            {successMessage && <Text style={styles.successText}>{successMessage}</Text>}
          </ScrollView>

          {pending && !successMessage && (
            <View style={styles.previewBlock}>
              <Image source={{ uri: pending.uri }} style={styles.previewImage} resizeMode="cover" />
              <Text style={styles.previewMeta}>
                {pending.width} × {pending.height} px · {formatBytes(pending.sizeBytes)}
              </Text>
            </View>
          )}

          <View style={styles.footer}>
            {!successMessage ? (
              <>
                <TouchableOpacity style={styles.cancelButton} onPress={handleClose} activeOpacity={0.85}>
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.acceptButton, !pending && styles.acceptButtonDisabled]}
                  onPress={handleAccept}
                  disabled={!pending || isBusy}
                  activeOpacity={0.85}
                >
                  <Text style={styles.acceptButtonText}>Accept</Text>
                </TouchableOpacity>
              </>
            ) : (
              <TouchableOpacity style={styles.doneButton} onPress={handleDoneAfterSuccess} activeOpacity={0.85}>
                <Text style={styles.doneButtonText}>Close</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};
